import React from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function formatCardDueDate(isoDate) {
  if (!isoDate) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return isoDate;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function tagPreview(tags) {
  if (tags == null) return null;
  if (Array.isArray(tags)) {
    const slice = tags.slice(0, 3);
    return slice.map((t, i) => (
      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 mr-1">
        {typeof t === 'string' ? t : JSON.stringify(t)}
      </span>
    ));
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">JSON</span>
  );
}

function descriptionPreview(description) {
  const t = typeof description === 'string' ? description.trim() : '';
  if (!t) return null;
  return (
    <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" title={t}>
      {t}
    </div>
  );
}

export default function SortableProjectCard({ id, card, onClick, showDescription = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const tagsPreviewEl = tagPreview(card.tags);
  const descriptionEl = showDescription ? descriptionPreview(card.description) : null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const mergedStyle = {
    ...style,
    borderLeftWidth: 4,
    borderLeftColor: card.color || '#E5E7EB',
  };

  return (
    <div
      ref={setNodeRef}
      style={mergedStyle}
      className="touch-none flex min-h-14 rounded-lg border border-gray-200 shadow-sm bg-white overflow-hidden hover:border-blue-300 transition"
    >
      <button
        type="button"
        className="px-2 py-2 text-gray-400 hover:bg-gray-50 cursor-grab active:cursor-grabbing shrink-0 self-stretch flex items-center"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-5 w-5" strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={() => onClick(card)}
        className="flex-1 text-left py-2 pr-3 min-w-0"
      >
        <div className="font-medium text-sm text-gray-900 line-clamp-2">{card.title}</div>
        {descriptionEl}
        {card.due_date && (
          <div className={`text-[11px] text-gray-500 ${descriptionEl ? 'mt-1' : 'mt-0.5'}`}>{formatCardDueDate(card.due_date)}</div>
        )}
        {tagsPreviewEl ? (
          <div className={`flex flex-wrap gap-0.5 ${card.due_date || descriptionEl ? 'mt-1.5' : 'mt-1'}`}>{tagsPreviewEl}</div>
        ) : null}
      </button>
    </div>
  );
}
